import { useEffect, useRef, useState } from 'react';
import { api, errMessage } from '../../../../utils/api';
import type { RefineImageResponse, ImageRefDTO } from '@historyjki/shared';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../ui/Spinner';

interface GenerateStepProps {
  imageRef: ImageRefDTO;
  updatedDescription: string;
  selectedElementIds: string[];
  intent: string;
  /** How many alternatives to generate in parallel (1–3). */
  variantCount: number;
  onComplete: (candidateR2Key: string, candidateImageUrl: string) => void;
  onBack: () => void;
}

interface Variant {
  r2Key: string;
  imageUrl: string;
}

/**
 * Auto-runs `refine-image` on mount, generating `variantCount` alternatives in
 * parallel. Guarded against React 19 StrictMode double-mounts so a single step
 * transition doesn't burn duplicate Gemini calls.
 *
 * - 0 succeed → error with a retry.
 * - 1 succeeds → advance straight to compare.
 * - 2+ succeed → show a picker grid; the unpicked candidates are rejected.
 */
export function GenerateStep({
  imageRef,
  updatedDescription,
  selectedElementIds,
  intent,
  variantCount,
  onComplete,
  onBack,
}: GenerateStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [attempt, setAttempt] = useState(0);
  // Tracks the current attempt id so stale promise resolutions (e.g. from
  // React 19's StrictMode double-mount in dev) are ignored without ever
  // cancelling the in-flight refine calls before onComplete can fire.
  const attemptIdRef = useRef(0);

  const rejectCandidate = (r2Key: string) => {
    api
      .post(`/projects/${imageRef.project_id}/refine/reject-refinement`, {
        image_ref: imageRef,
        candidate_r2_key: r2Key,
      })
      .catch(() => {});
  };

  useEffect(() => {
    attemptIdRef.current += 1;
    const myAttempt = attemptIdRef.current;
    const count = Math.max(1, Math.min(3, variantCount));

    (async () => {
      setError(null);
      setVariants([]);

      const results = await Promise.allSettled(
        Array.from({ length: count }, () =>
          api.post<RefineImageResponse>(
            `/projects/${imageRef.project_id}/refine/refine-image`,
            {
              image_ref: imageRef,
              updated_description: updatedDescription,
              selected_element_ids: selectedElementIds,
              intent,
            },
          ),
        ),
      );

      if (myAttempt !== attemptIdRef.current) return;

      const ok: Variant[] = [];
      let firstError: unknown = null;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          ok.push({ r2Key: r.value.r2_key, imageUrl: r.value.image_url });
        } else if (!firstError) {
          firstError = r.reason;
        }
      }

      if (ok.length === 0) {
        setError(errMessage(firstError, 'Image generation failed'));
      } else if (ok.length === 1) {
        onComplete(ok[0].r2Key, ok[0].imageUrl);
      } else {
        setVariants(ok);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const retry = () => {
    setAttempt((n) => n + 1);
  };

  const pick = (chosen: Variant) => {
    for (const v of variants) {
      if (v.r2Key !== chosen.r2Key) rejectCandidate(v.r2Key);
    }
    onComplete(chosen.r2Key, chosen.imageUrl);
  };

  const backFromPicking = () => {
    for (const v of variants) rejectCandidate(v.r2Key);
    onBack();
  };

  // ── Picker grid (2+ alternatives generated) ──
  if (variants.length > 0) {
    return (
      <div className="refine-step refine-step--generating">
        <h3 className="refine-step__title">Pick the alternative you prefer</h3>
        <div className="refine-step__variants-grid">
          {variants.map((v, i) => (
            <button
              key={v.r2Key}
              type="button"
              className="refine-step__variant-thumb"
              onClick={() => pick(v)}
              title={`Use alternative ${i + 1}`}
            >
              <img
                src={v.imageUrl}
                alt={`Alternative ${i + 1}`}
                className="refine-step__variant-img"
              />
            </button>
          ))}
        </div>
        <div className="refine-step__actions">
          <Button variant="ghost" onClick={backFromPicking}>
            &larr; Back
          </Button>
        </div>
      </div>
    );
  }

  const count = Math.max(1, Math.min(3, variantCount));

  return (
    <div className="refine-step refine-step--generating">
      <h3 className="refine-step__title">Generating refined image</h3>
      {error ? (
        <>
          <p className="refine-step__error">{error}</p>
          <div className="refine-step__actions">
            <Button variant="ghost" onClick={onBack}>
              &larr; Back
            </Button>
            <Button onClick={retry}>Retry</Button>
          </div>
        </>
      ) : (
        <div className="refine-step__loading">
          <Spinner size="lg" />
          <p>
            {count > 1
              ? `Generating ${count} alternatives…`
              : 'Applying your changes…'}
          </p>
          <p className="refine-step__help">
            This may take 10&ndash;30 seconds. The current image and your
            picked references are being sent to the image model.
          </p>
        </div>
      )}
    </div>
  );
}
